import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, ShoppingBag, MapPin, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { trackEvent } from '@/components/Analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_ADDR = {
  first_name: '', last_name: '', email: '', phone: '',
  address_line_1: '', address_line_2: '', city: '',
  postcode: '', country: 'United Kingdom',
};

function useCountdown(deadlineIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadlineIso) return null;
  const ms = new Date(deadlineIso).getTime() - now;
  if (ms <= 0) return { expired: true, days: 0, hours: 0, mins: 0, secs: 0 };
  return {
    expired: false,
    days: Math.floor(ms / 86400000),
    hours: Math.floor((ms / 3600000) % 24),
    mins: Math.floor((ms / 60000) % 60),
    secs: Math.floor((ms / 1000) % 60),
  };
}

export default function BumperOffer() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [addr, setAddr] = useState(DEFAULT_ADDR);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const quantity = 1; // bumper offer is single-bottle only
  const countdown = useCountdown(config?.deadline);

  useEffect(() => {
    axios.get(`${API}/bumper-offer/tiers`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.post(`${API}/bumper-offer/price`, { quantity: 1 }).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      // GA4 conversion event — fires before the Stripe redirect so it lands even if redirect is fast
      trackEvent('begin_checkout', {
        currency: 'GBP',
        value: 37.99,
        items: [{
          item_id: 'biocurcumax-bumper',
          item_name: 'BCM-95® — Welcome Bumper',
          price: 37.99,
          quantity: 1,
        }],
        is_bumper_offer: true,
      });
      trackEvent('bumper_offer_claim', { value: 37.99, currency: 'GBP' });
      const r = await axios.post(`${API}/bumper-offer/checkout`, {
        quantity: 1,
        delivery_address: addr,
        billing_same_as_delivery: true,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sorry — could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  if (!config) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>;

  if (!config.enabled) {
    return (
      <div className="min-h-screen py-24 text-center px-6">
        <h1 className="text-3xl font-bold mb-2 font-heading">Offer has ended</h1>
        <p className="text-muted-foreground mb-6">The Retailer Bumper Offer is no longer active.</p>
        <button onClick={() => navigate('/products')} className="px-6 py-3 rounded-xl bg-emerald-700 text-white font-semibold">View standard pricing</button>
      </div>
    );
  }

  const u = (k, v) => setAddr(a => ({ ...a, [k]: v }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/40 to-white py-12 md:py-20">
      <SEO
        title="First-Time Buyer Bumper Offer — £37.99 | IIY Phytoventra"
        description="First-time buyer welcome offer — BCM-95® for £37.99, single bottle, all-inclusive of UK VAT and FREE Royal Mail 48 delivery."
      />
      <div className="max-w-3xl mx-auto px-6">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-bold bg-amber-600 text-white mb-4 shadow-md">
            <Sparkles className="w-3 h-3" /> First-Time Buyer Exclusive
          </span>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-emerald-900 mb-3" data-testid="bumper-title">
            Your Welcome Bumper Offer
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            One bottle of BCM-95® at our exclusive first-time buyer price. UK VAT and Royal Mail 48 delivery are baked in &mdash; nothing else added at checkout.
          </p>
          {countdown && !countdown.expired && (
            <div className="mt-6 inline-flex flex-col items-center gap-1 px-5 py-2.5 rounded-2xl bg-red-50 border border-red-200 text-red-700" data-testid="bumper-countdown">
              <div className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold tracking-wide">
                  Ends in {countdown.days}d {String(countdown.hours).padStart(2,'0')}:{String(countdown.mins).padStart(2,'0')}:{String(countdown.secs).padStart(2,'0')}
                </span>
              </div>
              <span className="text-[11px] uppercase tracking-[0.18em] text-red-600/80 font-semibold">
                Offer ends 30 June 2026
              </span>
            </div>
          )}
        </motion.div>

        {/* Single-bottle hero card + summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-emerald-700 to-emerald-800 text-white rounded-3xl p-8 md:p-10 mb-8 shadow-xl" data-testid="bumper-single-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-emerald-200 font-semibold mb-2">Your One-Time Welcome Price</div>
              <div className="text-5xl md:text-6xl font-bold leading-none">£{pricing ? pricing.total.toFixed(2) : '37.99'}</div>
              <p className="text-emerald-100/90 text-sm mt-3 max-w-md">
                1 bottle &middot; 60 vegetarian capsules &middot; All-inclusive of UK VAT &amp; FREE Royal Mail 48 delivery.
              </p>
            </div>
            <div className="flex flex-col gap-2 text-xs text-emerald-100">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure Stripe payment</span>
              <span className="inline-flex items-center gap-1.5"><ShoppingBag className="w-3.5 h-3.5" /> 2-3 working day delivery</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> One-shot welcome price</span>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-white/20 text-[12px] text-emerald-100/80 leading-relaxed">
            Looking to buy more than one bottle? Our standard volume pricing offers up to <strong className="text-white">40% off</strong> on multi-bottle orders &mdash;{' '}
            <button type="button" onClick={() => navigate('/products')} className="underline font-semibold hover:text-white" data-testid="bumper-multi-bottle-link">
              see the product page
            </button>.
          </div>
        </motion.div>

        {/* Address form */}
        <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-soft p-6 md:p-8 border border-slate-200 mb-10" data-testid="bumper-form">
          <h3 className="text-xl font-bold mb-1 font-heading flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-700" /> Delivery Details</h3>
          <p className="text-sm text-muted-foreground mb-6">We&rsquo;ll send your order confirmation and tracking updates here.</p>

          <div className="grid md:grid-cols-2 gap-4">
            <input required value={addr.first_name} onChange={e => u('first_name', e.target.value)} placeholder="First name" data-testid="bumper-first-name" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none" />
            <input required value={addr.last_name} onChange={e => u('last_name', e.target.value)} placeholder="Last name" data-testid="bumper-last-name" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none" />
            <input required type="email" value={addr.email} onChange={e => u('email', e.target.value)} placeholder="Email address" data-testid="bumper-email" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none md:col-span-2" />
            <input required value={addr.phone} onChange={e => u('phone', e.target.value)} placeholder="Phone number" data-testid="bumper-phone" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none md:col-span-2" />
            <input required value={addr.address_line_1} onChange={e => u('address_line_1', e.target.value)} placeholder="Address line 1" data-testid="bumper-address1" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none md:col-span-2" />
            <input value={addr.address_line_2} onChange={e => u('address_line_2', e.target.value)} placeholder="Address line 2 (optional)" data-testid="bumper-address2" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none md:col-span-2" />
            <input required value={addr.city} onChange={e => u('city', e.target.value)} placeholder="City / town" data-testid="bumper-city" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none" />
            <input required value={addr.postcode} onChange={e => u('postcode', e.target.value.toUpperCase())} placeholder="Postcode" data-testid="bumper-postcode" className="px-4 py-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none uppercase" />
          </div>

          {err && <p className="mt-4 text-sm text-red-600" data-testid="bumper-err">{err}</p>}

          <button type="submit" disabled={busy || !pricing} data-testid="bumper-pay-button" className="mt-6 w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-bold py-4 rounded-xl text-lg flex items-center justify-center gap-2 shadow-lg transition-all">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingBag className="w-5 h-5" />}
            {busy ? 'Redirecting to Stripe…' : pricing ? `Pay £${pricing.total.toFixed(2)} Securely` : 'Loading…'}
          </button>
          <p className="text-[10px] text-center text-muted-foreground mt-2">Powered by Stripe. Your card details never touch our servers.</p>
        </motion.form>

        <div className="text-center text-xs text-muted-foreground space-y-1 max-w-2xl mx-auto">
          <p><strong>Earn while you invest in yourself</strong> &mdash; cashback rewards coming soon to your account dashboard.</p>
          <p>Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Keep out of reach of children.</p>
        </div>
      </div>
    </div>
  );
}
